import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Customer Credits tab — wallets (`GET /customers/:id/credits`) and paginated
 * transactions (`GET /customers/:id/credits/transactions`).
 *
 * Field names are the v4 backend verbatim (verified against staging):
 * - credits: customer_id as plain number, booleans flat, dates as "YYYY-MM-DD" or null
 * - transactions: credit_id / invoice_id as plain numbers, note/credit_name/employee_name
 *   as strings or null, services as array-of-objects
 *
 * Mutations: POST /customers/:id/credits (create wallet + deposit) and
 * PUT /customers/:id/credits/:creditId (update wallet settings).
 */

/* ── Query keys ────────────────────────────────────────────────────────── */

export const creditKeys = {
  credits: (id: number) => ['customers', 'credits', id] as const,
  transactions: (id: number, params: CreditTransactionParams) =>
    ['customers', 'credits', 'transactions', id, params] as const,
};

/* ── Types ─────────────────────────────────────────────────────────────── */

/** `GET /customers/:id/credits` — one credit wallet row (verified against staging). */
export interface CustomerCreditRow {
  credit_id: number;
  customer_id: number;
  service_ids: number[];
  credit_total: number;
  credit_used: number;
  credit_remaining: number;
  credit_warning_amount: number;
  credit_warningable: boolean;
  credit_lockable: boolean;
  credit_effective_date: string | null;
  credit_expiry_date: string | null;
  credit_name: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

export type CreditTransactionType = 'deposit' | 'payment' | 'refund' | 'adjustment';
export type CreditTransactionStatus = 'pending' | 'processing' | 'completed' | 'failed';

interface CreditTransactionService {
  service_id: number;
  service_name: string;
}

/**
 * `GET /customers/:id/credits/transactions` row.
 * pgtype fields serialize as plain scalars in Go's JSON output (verified in sibling endpoints).
 */
export interface CreditTransactionRow {
  credit_transaction_id: number;
  credit_id: number | null;
  invoice_id: number | null;
  credit_transaction_amount: number;
  credit_transaction_note: string | null;
  credit_transaction_type: CreditTransactionType | string;
  credit_transaction_status: CreditTransactionStatus | string;
  employee_id: number;
  credit_name: string | null;
  employee_name: string | null;
  services: CreditTransactionService[];
  created_at: string | null;
  updated_at: string | null;
}

export interface CreditTransactionParams {
  page?: number;
  page_size?: number;
  q?: string;
}

/** `POST /customers/:id/credits` — create credit wallet + initial deposit transaction. */
export interface CreateCreditPayload {
  credit_transaction: {
    /** Pass -1 so the backend auto-creates a new wallet. */
    credit_id: number;
    credit_transaction_amount: number;
    credit_transaction_type: CreditTransactionType;
    credit_transaction_note?: string;
  };
}

/** `PUT /customers/:id/credits/:creditId` — update wallet settings. */
export interface UpdateCreditPayload {
  credit_name?: string;
  credit_warning_amount?: number;
  credit_warningable?: boolean;
  credit_lockable?: boolean;
  credit_expiry_date?: string | null;
}

/* ── Queries ───────────────────────────────────────────────────────────── */

/** `GET /customers/:id/credits` — all credit wallets for one customer. */
export function useCustomerCredits(customerId: number) {
  return useQuery({
    queryKey: creditKeys.credits(customerId),
    enabled: !!customerId,
    queryFn: async (): Promise<ResponseArray<CustomerCreditRow>> => {
      const res = await dataCentralApi.get<ResponseArray<CustomerCreditRow>>(
        `/customers/${customerId}/credits`,
      );
      return res.data;
    },
  });
}

/** `GET /customers/:id/credits/transactions` — paginated transaction log. */
export function useCustomerCreditTransactions(
  customerId: number,
  params: CreditTransactionParams = {},
) {
  return useQuery({
    queryKey: creditKeys.transactions(customerId, params),
    enabled: !!customerId,
    queryFn: async (): Promise<ResponseArray<CreditTransactionRow>> => {
      const res = await dataCentralApi.get<ResponseArray<CreditTransactionRow>>(
        `/customers/${customerId}/credits/transactions?${qs.stringify({
          page: 1,
          page_size: 20,
          ...params,
        })}`,
      );
      return res.data;
    },
  });
}

/* ── Mutations ─────────────────────────────────────────────────────────── */

/** `POST /customers/:id/credits` — create credit wallet + initial transaction. */
export function useCreateCredit(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateCreditPayload): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/customers/${customerId}/credits`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: creditKeys.credits(customerId) });
      qc.invalidateQueries({ queryKey: ['customers', 'credits', 'transactions', customerId] });
    },
  });
}

/** `PUT /customers/:id/credits/:creditId` — edit wallet settings. */
export function useUpdateCredit(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      creditId: number;
      payload: UpdateCreditPayload;
    }): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.put<ResponseObject<unknown>>(
        `/customers/${customerId}/credits/${vars.creditId}`,
        vars.payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: creditKeys.credits(customerId) });
    },
  });
}
